import {
  SendBackReasonMessages,
  SendBackSubReasonMessages,
} from '@bechtel/client-weld-common/src/components/atoms/i18n-message-keys';
import {
  Avatar,
  AvatarSize,
  Box,
  ButtonAppearance,
  ButtonSize,
  Heading,
  Icon,
  IconSize,
  Modal,
  ModalBody,
  ModalClose,
  ModalFooter,
  ModalHeader,
  ModalSize,
  ModalSubHeader,
  Separator,
  SeparatorType,
  Text,
  useModalState,
} from '@bechtel/gds';
import {
  BlockStatusCode,
  CommodityStatusCode,
  MilestoneActivity,
  MilestoneStatusCode,
  RejectWeldReason,
  RejectWeldSubReason,
} from '@bechtel/types';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { join, map, orderBy } from 'lodash';
import { FC } from 'react';
import {
  CommentHistory,
  FooterButtonStyled,
  MilestoneActivityMessages,
  MilestoneStatusMessages,
  TitleValue,
  WeldStatusMessages,
} from '~/components';
import { EllipsisPlacement, TextWithEllipsis } from '~/components/atoms/text-with-ellipsis';
import { AIIcon } from '~/components/organisms/ai-assistant-widget/ai-icon';
import { ImageViewer, useImageViewerState } from '~/components/organisms/image-viewer';
import { useDate } from '~/hooks/use-date';
import { useName } from '~/hooks/use-name';
import { WeldMilestoneType } from '~/types';
import {
  ActivityIconPattern,
  ArrowStyled,
  AttachmentButton,
  AttachmentsCountStyled,
  CommentStyled,
  FinalActivity,
  PipeCleaninessStyled,
  PriorityActivityBox,
  ReasonAlignmentStyled,
} from './styled';
import { ActivityProps, BlockCommentsHistoryProps, HistoryItemProps } from './types';

// Component to render activity icon
const ActivityIcon: FC<{
  milestoneName?: string | null;
  activity?: string | null;
  milestoneStatus?: MilestoneStatusCode | null;
}> = ({ milestoneName, activity, milestoneStatus }) => {
  // Check if this is a priority activity
  if (milestoneName === 'Priority' && (activity === 'ASSIGNED' || activity === 'UNASSIGNED')) {
    return (
      <PriorityActivityBox activity={activity}>
        <Icon icon="flame" size={IconSize.XSMALL} fillColor="white" />
      </PriorityActivityBox>
    );
  }

  // Default to the styled pattern for milestone statuses
  return <ActivityIconPattern status={milestoneStatus} />;
};

export const HistoryItem: FC<HistoryItemProps> = ({
  milestoneStatus,
  activity,
  attachments,
  isWeldCommodity,
  milestoneName,
  crewMember,
  last,
  id,
  dateTime,
  comments,
  rejectionReason,
  rejectionSubReasons,
  currentItrStatus,
  blockComments,
  workPackageTag,
  commodityTag,
  commodityName,
  aiPlanningRequestId,
  planningAiComment,
}) => {
  const { formatISODate, formatTime } = useDate();
  const { getFullName } = useName();
  const time = formatTime(dateTime);
  const day = formatISODate(dateTime);
  const { i18n } = useLingui();
  const isFitupMilestone = milestoneName?.toUpperCase() === WeldMilestoneType.FITUP;
  const { open: openImageViewer, close: closeImageViewer, visible: visibleImageViewer } = useImageViewerState(false);
  const { open: openCommentsModal, close: closeCommentsModal, visible: visibleCommentsModal } = useModalState(false);

  const milestoneStatusText =
    milestoneStatus &&
    (milestoneStatus == MilestoneStatusCode.REJECTED && isWeldCommodity
      ? i18n._(
          WeldStatusMessages[
            isFitupMilestone ? CommodityStatusCode.SENT_BACK_TO_PIPING : CommodityStatusCode.VT_SENT_BACK
          ]
        )
      : i18n._(MilestoneStatusMessages[milestoneStatus]));
  const milestoneActivityText =
    activity &&
    (activity == MilestoneActivity.REJECTED && isWeldCommodity
      ? i18n._(
          WeldStatusMessages[
            isFitupMilestone ? CommodityStatusCode.SENT_BACK_TO_PIPING : CommodityStatusCode.VT_SENT_BACK
          ]
        )
      : i18n._(MilestoneActivityMessages[activity]));
  const otherSubReason = join(
    map(rejectionSubReasons, (item) => {
      return item.reason == RejectWeldSubReason.OTHER_SUB_REASON
        ? item.comments
        : i18n._(SendBackSubReasonMessages[item.reason]);
    }),
    ', '
  );

  const pipeCleanInspectionTooltipValue = currentItrStatus
    ? `${getFullName(
        currentItrStatus?.pipeCleanInspectionBy?.firstName,
        currentItrStatus?.pipeCleanInspectionBy?.lastName
      )}\n${formatISODate(currentItrStatus?.pipeCleanInspectionDate)}\n${formatTime(
        currentItrStatus?.pipeCleanInspectionDate
      )}`
    : '';
  return (
    <Box mb={{ sm: '600' }} data-testid={`commodity-activity-item-${id}`}>
      {/* Min width is required to align the content vertically as same text length creating different width in the DOM */}
      <Box
        flexDirection={{ sm: 'column' }}
        alignItems={{ sm: 'flex-end' }}
        mt={{ sm: '400' }}
        pl={{ sm: '400' }}
        style={{ width: '112px' }}
      >
        {activity && (
          <Box mb={{ sm: '200' }}>
            <Text size="300" weight="600" color="grey.1000">
              {milestoneName === 'Priority' ? (
                activity === 'ASSIGNED' ? (
                  <Trans>Assigned</Trans>
                ) : (
                  <Trans>Unassigned</Trans>
                )
              ) : milestoneStatus === MilestoneStatusCode.BLOCKED ? (
                i18n._(MilestoneStatusMessages[MilestoneStatusCode.BLOCKED])
              ) : (
                milestoneActivityText
              )}
            </Text>
            &nbsp;
            <Text size="300" color="grey.1000">
              <Trans>on</Trans>
            </Text>
          </Box>
        )}
        <Box mb={{ sm: '200' }}>
          <Text size="250" color="grey.600">
            {time && time.toLowerCase()}
          </Text>
        </Box>
        <Text size="250" color="grey.600">
          {day}
        </Text>
      </Box>
      <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} mt={{ sm: '200' }} mx={{ sm: '400' }}>
        <Box mb={{ sm: '300' }}>
          <ActivityIcon milestoneStatus={milestoneStatus} activity={activity} milestoneName={milestoneName} />
        </Box>
        {/*  @ts-ignore: GDS typing update needed */}
        <Separator as="div" size="95%" borderWidth="1px" color="bechtel.blue.700" type={SeparatorType.VERTICAL} />
        {last && (
          <Box>
            <FinalActivity />
          </Box>
        )}
      </Box>
      <Box flexDirection={{ sm: 'column' }}>
        <Box mt={{ sm: '400' }}>
          <Text size="300" weight="600" color="grey.1000">
            {milestoneName}
          </Text>
          {milestoneName ? (
            <Text size="300" color="grey.600" transform="capitalize">
              :&nbsp;
              {milestoneName === 'Priority' ? (
                activity === 'ASSIGNED' ? (
                  <Trans>Assigned</Trans>
                ) : (
                  <Trans>Unassigned</Trans>
                )
              ) : (
                milestoneStatusText
              )}
            </Text>
          ) : (
            <Text size="400" color="grey.800" weight="600" family="graphik" transform="capitalize">
              {milestoneStatusText}
            </Text>
          )}
          {aiPlanningRequestId && !planningAiComment && activity === MilestoneActivity.PLANNED && (
            <Box ml={{ sm: '200' }} alignItems={{ sm: 'center' }} title={t`AI Planned`}>
              <AIIcon size={16} color="blueviolet" />
            </Box>
          )}
        </Box>
        {currentItrStatus && (
          <Box mt={{ sm: '450' }} mr={{ sm: '500' }}>
            <PipeCleaninessStyled>
              <Icon icon="circle-check" size={IconSize.XSMALL} />
              <Box ml={{ sm: '200' }}>
                <TextWithEllipsis
                  family="graphik"
                  color="green.800"
                  size="200"
                  ellipsisPlacement={EllipsisPlacement.END}
                  title={pipeCleanInspectionTooltipValue}
                >
                  <Trans>Pipe Cleanliness</Trans>
                </TextWithEllipsis>
              </Box>
            </PipeCleaninessStyled>
          </Box>
        )}
        <Box mt={{ sm: '400' }}>
          <Box mr={{ sm: '400' }}>
            <Avatar src={crewMember?.imageUrl} size={AvatarSize.MEDIUM} />
          </Box>
          <Box flexDirection={{ sm: 'column' }} justifyContent={{ sm: 'space-around' }}>
            <Text size="250" color="grey.600">
              {getFullName(crewMember?.firstName, crewMember?.lastName)}
            </Text>
          </Box>
          {!!attachments?.length && (
            <Box ml={{ sm: '550' }}>
              <AttachmentsCountStyled alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
                {attachments?.length}
              </AttachmentsCountStyled>
              <AttachmentButton
                appearance={ButtonAppearance.SECONDARY}
                size={ButtonSize.SMALL}
                onClick={() => {
                  openImageViewer();
                }}
              >
                <Icon icon="paperclip" size={IconSize.MEDIUM} />
              </AttachmentButton>
            </Box>
          )}
          {milestoneStatus === MilestoneStatusCode.BLOCKED && blockComments && !!blockComments.length && (
            <Box ml={attachments && attachments?.length > 0 ? { sm: '100' } : { sm: '550' }}>
              <AttachmentsCountStyled alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
                {blockComments.length}
              </AttachmentsCountStyled>
              <AttachmentButton
                appearance={ButtonAppearance.SECONDARY}
                size={ButtonSize.SMALL}
                onClick={() => {
                  openCommentsModal();
                }}
              >
                <Icon icon="comment-alt" iconStyle="solid" size={IconSize.MEDIUM} />
              </AttachmentButton>
            </Box>
          )}
        </Box>
        {visibleImageViewer && (
          <ImageViewer
            selectedImageIndex={0}
            images={map(attachments, 'url')}
            visible={visibleImageViewer}
            closable={true}
            onDismiss={closeImageViewer}
          />
        )}
        {visibleCommentsModal && (
          <BlockCommentsHistory
            blockComments={blockComments && blockComments.length > 0 ? blockComments : []}
            blockStatus={BlockStatusCode.BLOCK_OPENED}
            onClose={closeCommentsModal}
            commodityTag={commodityTag}
            workPackageTag={workPackageTag}
            commodityName={commodityName}
            milestoneName={milestoneName}
          />
        )}
        {rejectionReason?.reason !== RejectWeldReason.OTHER_REASON && comments && (
          <Box mt={{ sm: '400' }} mr={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
            <ArrowStyled />
            <CommentStyled style={{ width: '200px', wordBreak: 'keep-all' }}>
              <Text title={comments}>{comments}</Text>
            </CommentStyled>
          </Box>
        )}
        {planningAiComment && (
          <Box mt={{ sm: '400' }} mr={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
            <ArrowStyled />
            <CommentStyled style={{ width: '200px', wordBreak: 'keep-all' }}>
              <Box mb={{ sm: '100' }}>
                <Text size="250" color="grey.600" weight="600">
                  <Trans>Re-plan reason:</Trans>
                </Text>
              </Box>
              <Text size="250" title={planningAiComment}>
                {planningAiComment}
              </Text>
            </CommentStyled>
          </Box>
        )}
        {rejectionReason && (
          <Box mt={{ sm: '400' }} mr={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
            <ArrowStyled />
            <CommentStyled>
              <ReasonAlignmentStyled>
                <Text size="250" color="grey.1000" weight="600">
                  <Trans>Reason:</Trans> &nbsp;
                </Text>
                <Text
                  size="250"
                  title={
                    rejectionReason?.reason == RejectWeldReason.OTHER_REASON
                      ? rejectionReason.comments
                      : i18n._(SendBackReasonMessages[rejectionReason?.reason])
                  }
                >
                  {rejectionReason?.reason == RejectWeldReason.OTHER_REASON
                    ? rejectionReason.comments
                    : i18n._(SendBackReasonMessages[rejectionReason?.reason])}
                </Text>
              </ReasonAlignmentStyled>
              {rejectionSubReasons && (
                <ReasonAlignmentStyled pt={{ sm: '300' }}>
                  <Text size="250" color="grey.1000" weight="600" style={{ whiteSpace: 'nowrap' }}>
                    <Trans> Sub Reason(s):</Trans>&nbsp;
                  </Text>
                  <Text size="250" title={otherSubReason}>
                    {otherSubReason}
                  </Text>
                </ReasonAlignmentStyled>
              )}
            </CommentStyled>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const BlockCommentsHistory: FC<BlockCommentsHistoryProps> = ({
  blockComments,
  blockStatus,
  onClose,
  workPackageTag,
  commodityTag,
  commodityName,
  milestoneName,
}) => {
  const orderedComments = orderBy(blockComments, ['createdAt'], ['desc']);
  return (
    <Modal visible size={ModalSize.DEFAULT} style={{ width: '552px', height: '552px' }}>
      <ModalHeader title={t`Comments`}>
        <ModalClose onClick={onClose}></ModalClose>
      </ModalHeader>
      <ModalSubHeader>
        <Box mt={{ sm: '400' }}>
          <TitleValue title={t`Work Package`} value={workPackageTag} size="500" />
        </Box>
        {commodityName && commodityTag && (
          <Box mt={{ sm: '400' }}>
            <TitleValue title={commodityName} value={commodityTag} size="500" />
          </Box>
        )}
        {milestoneName && (
          <Box mt={{ sm: '400' }}>
            <TitleValue title={t`Milestone`} value={milestoneName} size="500" />
          </Box>
        )}
      </ModalSubHeader>
      <ModalBody>
        <CommentHistory blockComments={orderedComments} blockStatus={blockStatus} canEditBlock={false} />
      </ModalBody>
      <ModalFooter>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box mr={{ sm: '400' }}>
            <FooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={onClose}>
              <Trans>Close</Trans>
            </FooterButtonStyled>
          </Box>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export const ActivityHistory: FC<ActivityProps> = ({
  activityTitle,
  milestonesActivityHistory,
  isWeldCommodity,
  workPackageTag,
  commodityTag,
  commodityName,
}) => {
  return (
    <>
      {activityTitle && (
        <Box m={{ sm: '500' }}>
          <Heading color="grey.1000" weight="600" data-testid="commodity-activity-header">
            {activityTitle}
          </Heading>
        </Box>
      )}
      <Box alignItems={{ sm: 'center' }} flexDirection={{ sm: 'column' }}>
        <Box flexDirection={{ sm: 'column' }} mt={{ sm: '500' }} data-testid="commodity-activity-history">
          {map(milestonesActivityHistory, (activity, index: number) => (
            <HistoryItem
              key={activity.id}
              {...activity}
              last={milestonesActivityHistory.length === index + 1}
              isWeldCommodity={isWeldCommodity}
              commodityName={commodityName}
              workPackageTag={workPackageTag}
              commodityTag={commodityTag}
            />
          ))}
        </Box>
      </Box>
    </>
  );
};
